/**
 * data/tuning.ts — the single source of every Phase 0/1 tunable; systems import from
 * here so no gameplay number is ever spelled twice.
 */

// Declared shapes are widened to `number` on purpose. `as const` would give each field a
// literal type, and `let t = MOVEMENT.turnRateRest; t = MOVEMENT.turnRateFull;` would then
// fail to compile in every consumer. Readonly members keep the objects immutable anyway.

interface MovementTuning {
  readonly cruiseSpeed: number;
  readonly acceleration: number;
  readonly reverseThrustScale: number;
  readonly turnRateRest: number;
  readonly turnRateFull: number;
  readonly lateralDamp: number;
  readonly linearDrag: number;
  readonly quadDrag: number;
  readonly glideDrag: number;
  readonly bankMax: number;
  readonly bankResponse: number;
  readonly pitchFromAccel: number;
  readonly bobAmplitude: number;
  readonly bobFrequency: number;
  readonly swimHeight: number;
}

// Hoisted so quadDrag can be a real derived number rather than a comment.
const CRUISE_SPEED = 11;
const ACCELERATION = 34;
const LINEAR_DRAG = 0.55;

export const MOVEMENT: MovementTuning = {
  cruiseSpeed: CRUISE_SPEED,       // u/s   steady-state top speed under full thrust (SPEC §5.2)
  acceleration: ACCELERATION,      // u/s^2 forward thrust (SPEC §5.2)
  reverseThrustScale: 0.25,        // thrust scale when input points behind you — reversing is weak, you must turn
  turnRateRest: 220,               // deg/s at a standstill (SPEC §5.2)
  turnRateFull: 95,                // deg/s at cruise and above — speed costs agility, that is the whole trade
  lateralDamp: 7.0,                // 1/s keel drag; kills sideways slip and is what makes the shark a boat, not a cursor
  linearDrag: LINEAR_DRAG,         // 1/s viscous bleed, dominates at low speed where quadDrag is negligible
  // Equilibrium drag: full thrust balances against BOTH drag terms exactly at cruiseSpeed,
  // so 11 u/s falls out of the physics instead of being clamped. Solving
  // acceleration = quadDrag*v^2 + linearDrag*v at v = cruiseSpeed is what makes ventilation
  // actually reach 1.0 — dropping the linear term here tops the shark out at 10.07 u/s.
  quadDrag: (ACCELERATION - LINEAR_DRAG * CRUISE_SPEED) / (CRUISE_SPEED * CRUISE_SPEED), // 0.2310 1/u
  glideDrag: 0.85,                 // 1/s extra bleed with no input at all — coasting decays, ram ventilation punishes it (SPEC §5.1)
  bankMax: 0.34,                   // rad (~19 deg) of roll in a full-speed carve — "sharks bank" (SPEC §5.2)
  bankResponse: 6.0,               // 1/s lerp toward target bank; slower than the turn so the roll trails the yaw
  pitchFromAccel: 0.10,            // rad per u/s^2 of forward accel, clamped to +/-0.25 — nose lifts under thrust
  bobAmplitude: 0.4,               // u vertical sine, cosmetic only (SPEC §5.2 "Vertical: cosmetic")
  bobFrequency: 1.15,              // Hz of that bob
  swimHeight: 3.0,                 // u above the seafloor the body rides; meshes are Y-centred so 0 buries it
};

interface LungeTuning {
  readonly distance: number;
  readonly duration: number;
  readonly iframes: number;
  readonly cooldown: number;
  readonly exitSpeedScale: number;
  readonly ampSpike: number;
  readonly ampSpikeTime: number;
  readonly fovPunch: number;
  readonly fovPunchTime: number;
  readonly ghostCount: number;
  readonly ghostLife: number;
}

export const LUNGE: LungeTuning = {
  distance: 22,                    // u of ground covered by the dash (SPEC §5.2)
  duration: 0.22,                  // s — 100 u/s average, roughly 9x cruise, so it reads as a burst
  iframes: 0.15,                   // s invulnerable, ends before the dash does so it is not a free escape
  cooldown: 3.5,                   // s; executions refund 1.2 s of this (SPEC §5.3)
  exitSpeedScale: 1.35,            // x cruise retained on exit — you come out of a lunge already moving, never stalled
  ampSpike: 2.6,                   // x swim-shader amplitude during the thrash (SPEC §4.3)
  ampSpikeTime: 0.12,              // s of that spike, then eased back at SWIM.ampResponse
  fovPunch: 8,                     // deg on top of CAMERA.fov: 68 -> 76 -> 68 (SPEC §13)
  fovPunchTime: 0.30,              // s for the full out-and-back
  ghostCount: 3,                   // trailing ghost meshes (SPEC §13 "3 samples")
  ghostLife: 0.28,                 // s fade per ghost, slightly longer than the lunge so the trail outlives it
};

interface CameraTuning {
  readonly pitchDeg: number;
  readonly distanceNear: number;
  readonly distanceFar: number;
  readonly distanceResponse: number;
  readonly followResponse: number;
  readonly cursorLead: number;
  readonly cursorLeadResponse: number;
  readonly velocityDrift: number;
  readonly fov: number;
  readonly near: number;
  readonly far: number;
  readonly shakeDecay: number;
}

export const CAMERA: CameraTuning = {
  pitchDeg: 62,                    // deg down from horizontal (SPEC §13) — steep enough to read the arena, shallow enough to keep silhouettes
  distanceNear: 26,                // u at rest (SPEC §13)
  distanceFar: 34,                 // u at cruise — pulling back with speed buys reaction time
  distanceResponse: 2.2,           // 1/s; deliberately sluggish so the zoom is felt, not seen
  followResponse: 9.0,             // 1/s toward the player; fast enough that the shark never leaves the centre third
  cursorLead: 4.0,                 // u max offset toward the cursor (SPEC §13)
  cursorLeadResponse: 5.0,         // 1/s lerp on that lead, so flicking the mouse does not snap the frame
  velocityDrift: 0.4,              // u per unit of speed/cruise, biases the frame ahead of travel (SPEC §13)
  fov: 68,                         // deg base vertical FOV
  near: 0.5,
  far: 600,                        // matches the default Seafloor extent so the plane never clips
  shakeDecay: 4.5,                 // 1/s exponential on summed sines — never random jitter (SPEC §13)
};

interface SwimTuning {
  readonly frequency: number;
  readonly speedIdle: number;
  readonly speedCruise: number;
  readonly ampIdle: number;
  readonly ampCruise: number;
  readonly stiffness: number;
  readonly ampResponse: number;
  readonly curveMax: number;
  readonly curveResponse: number;
  readonly curveAtCruise: number;
  readonly ampTurn: number;
  readonly speedTurn: number;
  readonly turnEffortAtCruise: number;
}

export const SWIM: SwimTuning = {
  frequency: 7.0,                  // waves along the body; ~1.1 full undulations nose-to-tail
  speedIdle: 3.2,                  // rad/s at rest — a parked shark still breathes
  speedCruise: 9.5,                // rad/s at cruise (SPEC §4.3 "a sprinting one thrashes")
  ampIdle: 0.10,                   // world units of tail sway at spineT = 1, at rest
  ampCruise: 0.46,                 // world units at cruise
  stiffness: 0.85,                 // how much the local slab yaws with the wave; below 1 or the tail shears
  ampResponse: 7.0,                // 1/s lerp toward target amplitude, so speed changes read as effort
  curveMax: 1.15,                  // u of tail offset in a full-rate turn — the body arcs, it does not pivot
  curveResponse: 5.0,              // 1/s toward that bend; slower than the yaw so the body trails the turn
  // Bend is strongest at a STANDSTILL, not at speed: a fast shark carves a wide arc with
  // little curvature, a stationary one folds into a C because that is how it pivots. This
  // is the fraction of curveMax remaining at cruise.
  curveAtCruise: 0.55,
  // Sculling. A fish turning on the spot drives the turn with its tail, so the stroke has
  // to pick up even though it is going nowhere — without this a pivot reads as a rigid yaw.
  ampTurn: 0.26,                   // u of extra tail sway at a full-deflection pivot
  speedTurn: 4.2,                  // rad/s of extra beat at that pivot
  turnEffortAtCruise: 0.35,        // how much of that sculling survives at cruise
};

interface VoxelTuning {
  readonly size: number;
  readonly detailScale: number;
  readonly maxShatterVoxels: number;
}

// Every genome dimension is expressed in voxels, so `detailScale` and `size` are two ends of
// one lever: shrink the grid, grow the voxel, and the shark stays the same size in world
// units while its surface area — and therefore its triangle count — falls by roughly the
// square. 0.65 was chosen by measurement, not taste: at 1.0 the voxels are smaller than the
// on-screen noise floor at the 26-34 u camera distance and the silhouette reads as a smudge.
const DETAIL_SCALE = 0.65;
// World size per authored voxel. Genome dimensions are multiplied by DETAIL_SCALE and world
// size is divided by it, so DETAIL_SCALE cancels out entirely here: world length is exactly
// `species.length * BASE_VOXEL`. This is therefore THE knob for how big the animals are.
const BASE_VOXEL = 0.184;

export const VOXEL: VoxelTuning = {
  size: BASE_VOXEL / DETAIL_SCALE, // world units per voxel; a shark lands at ~8.1 u long
  detailScale: DETAIL_SCALE,       // multiplier applied to every genome dimension in voxels
  maxShatterVoxels: 4096,          // surface voxels kept per creature for the death burst (SPEC §4.4)
};

interface WaterTuning {
  readonly fogColor: number;
  readonly fogDensity: number;
  readonly sunDir: readonly [number, number, number];
  readonly sunColor: number;
  readonly ambientTop: number;
  readonly ambientBottom: number;
  readonly rimColor: number;
  readonly exposure: number;
}

export const WATER: WaterTuning = {
  // The Sunlit Shelf is the CLEAR stratum: a few metres of bright water over pale sand.
  // `hydro` at full strength is the mid-water body colour of the deeper strata and turns the
  // shelf into soup, so this is hydro lifted and desaturated toward the surface light. The
  // cold, dense end of the range belongs to Kelp Cathedral and below (SPEC §2, §10.2).
  fogColor: 0x4a97a0,              // pale turquoise; also scene.background, so it is the horizon
  fogDensity: 0.0072,              // FogExp2; ~140 u of useful visibility — shallow, clear water
  // Unit vector pointing TOWARD the sun, i.e. usable directly as L in dot(N, L).
  // High and slightly astern-left so dorsal surfaces catch the key and the caustic dapple
  // has somewhere to fall. Pre-normalised to 5e-6.
  sunDir: [-0.3002, 0.9205, 0.2501],
  sunColor: 0xfff0d6,             // warm daylight; only a few metres of water have filtered it yet
  ambientTop: 0x7fb6c2,            // hemispheric sky term, lerped in by normal.y — bright, barely saturated
  ambientBottom: 0x1b4450,         // sand bounce; lifting this off `abyss` is what stops shadows going teal-black
  rimColor: 0x7fe3d4,              // `caustic` fresnel edge; the one thing that separates a shark from the fog
  exposure: 1.0,                   // ACES tone-mapping exposure
};

interface LabTuning {
  readonly spacing: number;
  readonly rowCount: number;
  readonly perRow: number;
  readonly turntableSpeed: number;
  readonly cameraDistance: number;
  readonly cameraHeight: number;
}

export const LAB: LabTuning = {
  spacing: 11,                     // u between sharks; ~1.5x the longest hull so silhouettes never touch
  rowCount: 2,                     // 2 x 5 = the ten species of SPEC §7
  perRow: 5,
  turntableSpeed: 0.35,            // rad/s — one revolution per ~18 s, slow enough to study a profile
  cameraDistance: 46,              // u back from the gallery centre
  cameraHeight: 20,                // u up; a shallower look than gameplay so the dorsal and caudal read
};

interface PerfTuning {
  readonly maxTriangleBudget: number;
}

export const PERF: PerfTuning = {
  maxTriangleBudget: 900,          // per shark; the Phase 1 acceptance gate (SPEC §4.2.3)
};

interface DepthTuning {
  readonly floorClearance: number;
  readonly cruiseFraction: number;
  readonly breachCeiling: number;
  readonly diveSpeed: number;
  readonly riseSpeed: number;
  readonly levelResponse: number;
  readonly response: number;
  readonly pitchPerSpeed: number;
  readonly breachSpan: number;
}

/**
 * SPEC §5.2 makes the vertical axis cosmetic and keeps gameplay on pure XZ. This build
 * promotes it to a real axis: you dive and surface, and the dorsal fin cuts the waterline
 * when you run shallow. Collision and combat stay on XZ — depth is traversal and drama.
 */
export const DEPTH: DepthTuning = {
  floorClearance: 3.0,             // u above the sand; the body is Y-centred so 0 buries it
  // Fraction of the AVAILABLE water column the shark settles into, measured up from its own
  // belly clearance. A fixed Y cannot work on a coast: the same number is mid-water in the
  // lagoon and buried in the reef flat.
  cruiseFraction: 0.42,
  breachCeiling: 30.8,             // u; OCEAN.surfaceHeight + a little, so the back clears the water
  diveSpeed: 11,                   // u/s descending
  riseSpeed: 9.5,                  // u/s climbing — heavier than diving, you fight buoyancy
  levelResponse: 0.55,             // 1/s drift back to cruise depth with no input; deliberately lazy
  response: 6.0,                   // 1/s smoothing on vertical velocity, so depth has weight
  pitchPerSpeed: 0.055,            // rad of nose pitch per u/s of climb — sells the dive
  breachSpan: 3.2,                 // u over which the fin goes from cutting to fully clear
};

interface OceanTuning {
  readonly surfaceHeight: number;
  readonly floorSegments: number;
  readonly duneAmplitude: number;
  readonly reefRise: number;
  readonly rippleAmplitude: number;
  readonly rippleWavelength: number;
  readonly gerstnerSteepness: number;
  readonly gerstnerAmplitude: number;
  readonly waveSpeed: number;
  readonly shaftCount: number;
  readonly shaftWidth: number;
  readonly shaftSway: number;
  readonly snowCount: number;
  readonly snowFallSpeed: number;
  readonly snowField: number;
  readonly propRadius: number;
  readonly propSpacing: number;
  readonly fishSchools: number;
  readonly fishPerSchool: number;
  readonly fishSpeed: number;
}

/**
 * The Sunlit Shelf's water column. SPEC §10 builds this per stratum in Phase 2; these are the
 * shallow-shelf values — surface close enough overhead to read, strong shafts, light silt.
 */
export const OCEAN: OceanTuning = {
  surfaceHeight: 30,               // u above the seafloor; close enough that the surface is legible overhead
  floorSegments: 260,              // grid resolution of the displaced floor; ~2.3 u quads over a 600 u plane
  duneAmplitude: 3.6,             // u of long-wavelength relief; the term you read as parallax at speed
  // How far the reef patches stand above the sand. Held down deliberately: the reef flat is
  // already the shallowest part of the profile, and rugosity multiplies this by up to 1.34,
  // so much more leaves no swimmable water over the crest and the reef becomes a wall.
  reefRise: 3.0,                   // u of lift inside a reef patch, before rugosity
  rippleAmplitude: 0.15,           // u of fine sand ripple; shading carries these, not silhouette
  // Crest spacing. At 2.6 the crests were close enough, and their slope steep enough,
  // that the whole shelf read as corduroy; the eye locks onto a regular fine grain long
  // before it reads it as sand. Wider and shallower is both calmer and more like a shelf.
  rippleWavelength: 6.5,           // u crest to crest
  // Shallow water: low, rounded wavelets. High steepness pinches crests into swell peaks,
  // which is the deep-ocean read; shallows want them nearly sinusoidal.
  gerstnerSteepness: 0.20,         // 0..1 sharpness of the surface wave crests
  gerstnerAmplitude: 0.13,         // u of vertical displacement on the tallest member
  // Phase speed. Shallow water is NON-dispersive — c = sqrt(g*h) regardless of wavelength —
  // so every wavelet travels together and the interference pattern holds its shape instead
  // of smearing the way a deep-water sqrt(g*k) set does. That coherence is most of what
  // reads as "shallows" rather than "sea".
  waveSpeed: 1.15,                 // u/s, uniform across every wavelength
  shaftCount: 9,                   // volumetric light shafts (SPEC §10.3 asks for 6-10)
  shaftWidth: 7.5,                 // u across at the surface
  shaftSway: 0.22,                 // rad of slow lateral swing
  snowCount: 6000,                 // marine snow motes across 3 parallax layers (SPEC §10.5)
  snowFallSpeed: 0.55,             // u/s downward drift
  snowField: 150,                  // u cube the snow recycles inside, centred on the camera
  propRadius: 150,                 // u radius of the prop scatter disc
  propSpacing: 12,                  // u minimum Poisson separation between props
  // Along a coast rather than around a disc, so the count is per WINDOW, not per world:
  // schools recycle past the player and these are the ones in front of you at any moment.
  fishSchools: 18,
  fishPerSchool: 8,
  fishSpeed: 3.4,                  // u/s; slow enough to read as ambience, not traffic
};
